/* eslint-disable jsx-a11y/media-has-caption */
import React, { useEffect, useState } from "react";

import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import VideoLibraryOutlinedIcon from "@mui/icons-material/VideoLibraryOutlined";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import {
  Popover,
  IconButton,
  Box,
  Typography,
  Select,
  MenuItem,
  Tooltip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import ButtonView from "../../../common/Button";
import { dashboardService } from "../../services";
import TableView from "../../../common/Table";
import { Data } from "./types";
import EpisodeRow from "./parts/EpisodeRow";
import { formatDurationToHHMMSS } from "../../../../utils/formatDurationToHHMMSS";
import { formatFileType } from "../../../../utils/formatFileType";
import { Column } from "../../../common/Table/types";
import { formatDateAndTime } from "../../../../utils/formatDate";
import { ProjectsWithMetadata } from "../../../Project/services/projects.service";
import { useWebSocket } from "../../../../hooks/useWebSocket";
import { WebSocketEventName } from "../../../common/enums/WebSocketEventName";
import { EpisodeStatus } from "../../../Episode/enums/EpisodeStatus";
import { usersService } from "../../../common/Headerbar/services";
import { RoleName } from "../../../common/enums/RoleName";
import RoleBasedAccess from "../../../common/RoleBasedAccess";
import { useUserState } from "../../../../store";
import { BRAND_COLORS } from "../../../../constants";

const columns: Column[] = [
  { id: "name", label: "Episode", minWidth: 200 },
  { id: "project", label: "Project", minWidth: 120 },
  { id: "upload", label: "Upload Date", minWidth: 140 },
  { id: "file_type", label: "Type", minWidth: 80 },
  { id: "duration", label: "Duration", minWidth: 100 },
  { id: "status", label: "Status", minWidth: 100 },
  { id: "view", label: "Actions", minWidth: 80 },
];

function createData(
  id: string,
  name: string,
  project: string,
  upload: string,
  file_type: string,
  duration: string,
  status: EpisodeStatus,
): Data {
  return { id, name, project, upload, file_type, duration, status };
}

const SKELETON_ROWS = 7;

const colWidths = ["30%", "14%", "16%", "7%", "10%", "10%", "8%"];

function TableSkeletonLoader() {
  return (
    <Table>
      <TableHead>
        <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
          {colWidths.map((w, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <TableCell key={i} sx={{ py: 1.25, px: 2, width: w }}>
              <Skeleton variant="text" width="60%" height={14} />
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: SKELETON_ROWS }).map((_, rowIdx) => (
          // eslint-disable-next-line react/no-array-index-key
          <TableRow key={rowIdx} sx={{ "&:last-child td": { border: 0 } }}>
            {/* Episode name cell — icon + two lines */}
            <TableCell sx={{ py: 1.5, px: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Skeleton
                  variant="rounded"
                  width={40}
                  height={40}
                  sx={{ borderRadius: "14px", flexShrink: 0 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="70%" height={14} />
                  <Skeleton
                    variant="text"
                    width="40%"
                    height={12}
                    sx={{ mt: 0.4 }}
                  />
                </Box>
              </Box>
            </TableCell>

            {/* Project */}
            <TableCell sx={{ py: 1.5, px: 2 }}>
              <Skeleton variant="text" width="75%" height={14} />
            </TableCell>

            {/* Upload date */}
            <TableCell sx={{ py: 1.5, px: 2 }}>
              <Skeleton variant="text" width="80%" height={14} />
            </TableCell>

            {/* Type chip */}
            <TableCell sx={{ py: 1.5, px: 2 }}>
              <Skeleton
                variant="rounded"
                width={48}
                height={22}
                sx={{ borderRadius: "12px" }}
              />
            </TableCell>

            {/* Duration */}
            <TableCell sx={{ py: 1.5, px: 2 }}>
              <Skeleton variant="text" width="65%" height={14} />
            </TableCell>

            {/* Status chip */}
            <TableCell sx={{ py: 1.5, px: 2 }}>
              <Skeleton
                variant="rounded"
                width={58}
                height={22}
                sx={{ borderRadius: "12px" }}
              />
            </TableCell>

            {/* Actions */}
            <TableCell sx={{ py: 1.5, px: 2 }}>
              <Box sx={{ display: "flex", gap: 0.75 }}>
                <Skeleton variant="circular" width={28} height={28} />
                <Skeleton variant="circular" width={28} height={28} />
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

interface Props {
  projects: ProjectsWithMetadata[] | undefined;
  refetchProjects: () => Promise<any>;
}

export default function EpisodeList({ projects, refetchProjects }: Props) {
  const { user } = useUserState();
  const navigate = useNavigate();
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const socket = useWebSocket();

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const [selectedVideo, setSelectedVideo] = React.useState("");
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    value: string,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedVideo(value);
  };

  const handleClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);
  const id = open ? "video-popover" : undefined;

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["episodes", selectedProjectId, page, rowsPerPage],
    queryFn: () =>
      dashboardService.fetchEpisodes({
        projectId: selectedProjectId,
        page,
        perPage: rowsPerPage,
      }),
  });

  const { refetch: refetchDashboard } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: () => dashboardService.fetchDashboardStats(),
  });

  const { refetch: refetchAuthenticatedUser } = useQuery({
    queryKey: ["authenticated-user"],
    queryFn: () => usersService.fetchAuthenticardUser(),
  });

  const handleRefreshEpisode = async () => {
    setIsRefreshing(true);
    try {
      const startTime = Date.now();
      await Promise.all([
        refetch(),
        refetchDashboard(),
        refetchAuthenticatedUser(),
      ]);
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < 500) {
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 500 - elapsedTime);
        });
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const wsEvents = [
    WebSocketEventName.EPISODE_INSIGHTS_GENERATED,
    WebSocketEventName.EPISODE_GENERATED,
    WebSocketEventName.EPISODE_DURATION_UPDATED,
    WebSocketEventName.EPISODE_PROCESSING_FAILED,
  ];

  useEffect(() => {
    if (!socket) return undefined;
    const handler = async () => {
      await refetch();
      await refetchDashboard();
      await refetchAuthenticatedUser();
    };
    wsEvents.forEach((event) => socket.on(event, handler));
    return () => {
      wsEvents.forEach((event) => socket.off(event, handler));
    };
  }, [socket]);

  const allRows =
    data?.episodes.map((episode) =>
      createData(
        episode.id,
        episode.name,
        episode.project.name,
        formatDateAndTime(episode.upload.created_at),
        formatFileType(episode.upload.mime_type),
        formatDurationToHHMMSS(episode.duration_in_milliseconds || 0),
        episode.status,
      ),
    ) || [];

  const rows = allRows;

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(1);
  };

  const totalItems = data?.pagination.total_items || 0;
  const isTableLoading = isLoading || isRefreshing;
  const isEmptyState = !isLoading && !isRefreshing && rows.length === 0;

  const renderEpisodesContent = () => {
    if (isTableLoading) {
      return (
        <TableContainer>
          <TableSkeletonLoader />
        </TableContainer>
      );
    }

    if (isEmptyState) {
      return (
        <Box
          sx={{
            px: 3,
            py: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(37, 99, 235, 0.08)",
              color: BRAND_COLORS.primary,
              mb: 1.5,
            }}
          >
            <VideoLibraryOutlinedIcon sx={{ fontSize: 34 }} />
          </Box>
          <Typography
            sx={{
              fontSize: "1rem",
              fontWeight: 700,
              color: "text.primary",
              mb: 0.5,
            }}
          >
            No episodes found
          </Typography>
          <Typography
            sx={{
              fontSize: "0.875rem",
              color: "text.secondary",
              mb: 2,
              maxWidth: 420,
            }}
          >
            Start by creating your first episode and uploading your content.
          </Typography>
          <RoleBasedAccess allowedRoles={[RoleName.ADMIN, RoleName.EDITOR]}>
            <ButtonView
              type="button"
              variant="contained"
              label="Upload Episode"
              disabled={
                user?.organization?.subscription?.available_credits === 0
              }
              startIcon={<AddOutlinedIcon sx={{ fontSize: 14 }} />}
              handleOnClick={() => navigate("/episodes/upload")}
              sx={{
                backgroundColor: BRAND_COLORS.primary,
                borderRadius: "8px",
                px: 2.5,
                py: 0.875,
                fontSize: "13px",
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "0 1px 3px rgba(37, 99, 235, 0.3)",
                "&:hover": { backgroundColor: BRAND_COLORS.primaryDark },
              }}
            />
          </RoleBasedAccess>
        </Box>
      );
    }

    return (
      <>
        <TableContainer>
          <TableView
            columns={columns}
            rows={rows}
            handleClick={handleClick}
            TableList={EpisodeRow}
            handleCustomEvent={() => {}}
          />
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton
          showLastButton
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            "& .MuiTablePagination-toolbar": { minHeight: 48 },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: "12px",
              },
          }}
        />
      </>
    );
  };
  return (
    <>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ width: "min(450px, 90vw)", display: "flex" }}>
          <video
            src={selectedVideo}
            autoPlay
            controls
            style={{ width: "100%", height: "auto", display: "block" }}
          />
        </Box>
      </Popover>

      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: { xs: "wrap", sm: "nowrap" },
        }}
      >
        {/* Project filter */}
        <Select
          value={selectedProjectId}
          onChange={(e) => setSelectedProjectId(e.target.value)}
          displayEmpty
          onOpen={() => refetchProjects()}
          size="small"
          IconComponent={KeyboardArrowDownIcon}
          sx={{
            backgroundColor: "#fff",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "8px",
            minWidth: 160,
            fontSize: "13px",
            fontWeight: 500,
            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            "& .MuiSelect-select": { py: 1, px: 1.5 },
            "& .MuiSvgIcon-root": {
              fontSize: 18,
              color: theme.palette.text.secondary,
            },
          }}
        >
          <MenuItem value="" sx={{ fontSize: "13px" }}>
            All Projects
          </MenuItem>
          {projects?.map((p) => (
            <MenuItem key={p.id} value={p.id} sx={{ fontSize: "13px" }}>
              {p.name}
            </MenuItem>
          ))}
        </Select>

        <Box sx={{ flex: 1 }} />

        {/* Refresh */}
        <Tooltip title="Refresh">
          <IconButton
            onClick={handleRefreshEpisode}
            disabled={isRefreshing}
            size="small"
            sx={{
              width: 36,
              height: 36,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "8px",
              backgroundColor: "#fff",
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
                color: BRAND_COLORS.primary,
              },
              "&:disabled": { backgroundColor: "#f9fafb" },
            }}
          >
            <RefreshIcon
              sx={{
                fontSize: 18,
                animation: isRefreshing ? "spin 0.8s linear infinite" : "none",
                "@keyframes spin": {
                  from: { transform: "rotate(0deg)" },
                  to: { transform: "rotate(360deg)" },
                },
              }}
            />
          </IconButton>
        </Tooltip>

        {/* Upload Episode */}
        <RoleBasedAccess allowedRoles={[RoleName.ADMIN, RoleName.EDITOR]}>
          <ButtonView
            type="button"
            variant="contained"
            size="small"
            label="Upload Episode"
            disabled={user?.organization?.subscription?.available_credits === 0}
            startIcon={<AddOutlinedIcon sx={{ fontSize: 14 }} />}
            handleOnClick={() => navigate("/episodes/upload")}
            sx={{
              backgroundColor: BRAND_COLORS.primary,
              borderRadius: "8px",
              px: 2,
              py: 0.75,
              fontSize: "13px",
              fontWeight: 600,
              height: 36,
              whiteSpace: "nowrap",
              boxShadow: "0 1px 3px rgba(37, 99, 235, 0.3)",
              "&:hover": { backgroundColor: BRAND_COLORS.primaryDark },
            }}
          />
        </RoleBasedAccess>
      </Box>

      {/* Table card */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        {/* Section header */}
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            All Episodes
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              color: theme.palette.text.secondary,
              mt: 0.25,
            }}
          >
            {totalItems} episodes total
          </Typography>
        </Box>

        {renderEpisodesContent()}
      </Paper>
    </>
  );
}
